import { CalculatedLine, MemberLine, RelativeGeometry } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

export function getDirectDependencyGuidsForRelativeGeometry(geometry: RelativeGeometry) {
  if (geometry.geometryType === 'Member') {
    return [geometry.lineGuid];
  }
  if (geometry.bearingGeometryType === 'Bearing') {
    return [geometry.bearingLineGuid, geometry.lineGuid];
  }
  if (geometry.bearingGeometryType === 'ContinuousBearing') {
    return [geometry.bearingLineGuid, geometry.leftSideLineGuid, geometry.rightSideLineGuid];
  }
  throw new Error('Unrecognized relative geometry type.');
}

export function getAllReferencedLineGuidsForRelativeGeometry(geometry: RelativeGeometry) {
  const directDependencyGuids = getDirectDependencyGuidsForRelativeGeometry(geometry);
  return geometry.geometryType === 'Member'
    ? [...directDependencyGuids, ...geometry.baseCutGuids, ...geometry.endCutGuids]
    : directDependencyGuids;
}

export function getDirectDependencyGuidsForNonMemberReferenceLine(
  line: Exclude<CalculatedLine, MemberLine>,
): (string | null)[] {
  switch (line.lineType) {
    case 'Absolute':
      return [];
    case 'Parallel':
      return [line.lineGuid];
    case 'PointSlope':
      return [line.line1Guid, line.line2Guid];
    case 'PointToPoint':
      return [line.basePointLine1Guid, line.basePointLine2Guid, line.endPointLine1Guid, line.endPointLine2Guid];
    case 'Perpendicular':
      return [line.basePointLine1Guid, line.basePointLine2Guid, line.lineGuid];
    case 'Division':
      return [line.line1Guid, line.line2Guid];
    default:
      assertExhaustiveSwitch(line, 'Unrecognized line type.');
  }
}

export function getDirectDependencyGuidsForReferenceLine(
  line: CalculatedLine,
  geometries: ReadonlyArray<RelativeGeometry>,
): (string | null)[] {
  switch (line.lineType) {
    case 'Absolute':
    case 'Parallel':
    case 'PointSlope':
    case 'PointToPoint':
    case 'Perpendicular':
    case 'Division':
      return getDirectDependencyGuidsForNonMemberReferenceLine(line);
    case 'Member': {
      const geometry = geometries.find((geometry) => geometry.guid === line.memberGeometryGuid);
      if (geometry === undefined) throw new Error('Could not find member geometry.');
      return getDirectDependencyGuidsForRelativeGeometry(geometry);
    }
    default: {
      assertExhaustiveSwitch(line, 'Unrecognized line type.');
    }
  }
}

export function getAllDependenciesOfCalculatedLine(
  line: CalculatedLine,
  referenceLines: readonly CalculatedLine[],
  geometries: readonly RelativeGeometry[],
  result: Set<string>,
) {
  for (const dependencyGuid of getDirectDependencyGuidsForReferenceLine(line, geometries)) {
    if (dependencyGuid == null || result.has(dependencyGuid)) {
      continue;
    }

    result.add(dependencyGuid);

    const dependencyLine = referenceLines.find((l) => l.guid === dependencyGuid);
    if (dependencyLine == null) {
      throw new Error('Unable to find reference line that existed very recently.');
    }

    getAllDependenciesOfCalculatedLine(dependencyLine, referenceLines, geometries, result);
  }
}

export function getAllDependentLinesOfCalculatedLine(
  line: CalculatedLine,
  referenceLines: readonly CalculatedLine[],
  geometries: readonly RelativeGeometry[],
): CalculatedLine[] {
  return referenceLines.filter((potentiallyDependentLine) => {
    const dependencyGuids = new Set<string>();
    getAllDependenciesOfCalculatedLine(potentiallyDependentLine, referenceLines, geometries, dependencyGuids);
    return dependencyGuids.has(line.guid);
  });
}
